import { MultiSelectOption } from './CommonTypes';
import { ResultStatus } from './QueryResult';

export interface Company {
  tenantId: string;
  version: string;
  integrationGUID: string;
  name: string;
  office: Office[];
  team: Team[];
  role: CompanyRole[];
  group: Group[];
  attributeDefinition: AttributeDefinition[];
  certificationAndLicense: CertificationAndLicense[];
  eventCategories: string[];
  settings?: Record<string, string>;
}
export interface StateProvince {
  id: number;
  state: string;
  abbreviation: string;
  isRequired?: boolean;
  isRequiredForStaff?: boolean;
}

export interface CertificationAndLicense {
  id: number;
  integrationGUID: string;
  name: string;
  description: string;
  organization: string;
  requireExpirationDate: boolean;
  requireDocumentation: boolean;
  verificationType: string;
  isActive: boolean;
  stateProvince: StateProvince[];
  certificationAndLicenseIntegrationGUID?: string;
  mode?: 'edit' | 'assign';
  resourceCertId?: string;
  externalCertificationAndLicenseId: string | null;
  isStateSpecific: boolean;
  isRequired?: boolean;
  isRequiredForStaff?: boolean;
}
export interface AttributeDefinition {
  type?: string;
  id: number;
  integrationGUID: string;
  displayName: string;
  isActive: boolean;
  attributeDataType: string;
  externalAttributeDefinitionId?: string;
  isDispatchPerilDefault?: boolean;
  isDispatchLanguageDefault?: boolean;
  attributeDefinitionValues: AttributeDefinitionValue[];
}

export enum GroupAttributeMatchMode {
  ANY = 'ANY',
  ALL = 'ALL',
}

export interface CompanyGroupAttributeValuesVarchars {
  integrationGUID?: string;
  selectedAttributeDefinitionIntegrationGUID: string;
  attributeDefinitionValueIntegrationGUID: string;
  title: string;
  value: string | undefined;
  displayName?: string;
}

export type CompanyGroupAttributes = {
  integrationGUID: string;
  displayName?: string;
  startDate?: string;
  endDate?: string;
  attributeDefinitionIntegrationGUID: string;
  selectedAttributeValuesVarchars: CompanyGroupAttributeValuesVarchars[];
  valueMatchModes?: Record<string, GroupAttributeMatchMode>;
};
export interface GroupAttributeDefinition {
  displayName: string;
  attributes?: CompanyGroupAttributes[]; //this is attributes definition inside each group attribute
  addSelectedAttributeGUID: MultiSelectOption[];
}

export interface AttributeDefinitionValue {
  id: number;
  integrationGUID: string;
  attributeDefinitionId: number;
  displayName: string;
  value: string;
  externalAttributeDefinitionValueId: string;
}

export interface AttributeOptions {
  value: string;
  title: string;
  valueType: string;
  attributeDefinitionValues: {
    value: string;
    title: string;
    definitionValue: AttributeDefinitionValue;
  }[];
  definition: AttributeDefinition;
}

export interface CompanyRole {
  integrationGUID: string;
  name?: string;
  id?: number;
  attributeDefinitionToRoleAssignment?: AttributeDefinitionToRoleAssignment[];
  certificateAndLicenseToRoleAssignment?: CertificateAndLicenseToRoleAssignment[];
  externalRoleId?: string;
}

export interface Office {
  integrationGUID: string;
  name: string;
}

export interface Team {
  id: string;
  integrationGUID: string;
  name: string;
  externalTeamId: string;
}

export interface Group {
  id: string;
  integrationGUID: string;
  name: string;
  externalGroupId: string;
}

export interface AttributeDefinitionToRoleAssignment {
  integrationGUID: string;
  id: number;
  roleId: number;
  attributeDefinitionId: number;
  attributeDefinitionValues: { id: number; integrationGUID: string }[];
}

export interface CertificateAndLicenseToRoleAssignment {
  integrationGUID: string;
  id: number;
  roleId: number;
  certificationAndLicenseId: number;
}

export interface CertsAndLicenseHookResponse {
  certsAndLicense: CertificationAndLicense[] | undefined;
  certsAndLicenseStatus: ResultStatus;
}

export type CompanyAdminMeta = {
  addAttribute: { open: boolean; data: AttributeDefinition | null };
  addGroupAttribute: { open: boolean; data: AttributeDefinition | null };
  delete: {
    open: boolean;
    data: AttributeDefinition | null;
    type?: string;
  };
  addResourceRole: { open: boolean; data: CompanyRole | null };
  addOffice: { open: boolean; data: Office | null };
  addTeam: { open: boolean; data: Team | null };
  addGroup: { open: boolean; data: Group | null };
  addCertAndLicense: { open: boolean; data: CertificationAndLicense | null };
};

export type DeleteBody = {
  integrationGUID: string;
  meta:
    | {
        open: boolean;
        data: (AttributeDefinition & { type: keyof Company }) | null;
      }
    | undefined;
};
